import type { IMultiCheckboxSubFormConfig } from "./MultiCheckboxSubForm.types";
import { dateFormat, i18nCrossUtil } from "@adgm/libs";

const crossIntl = i18nCrossUtil.t();

/**
 * IndividualSourceOfWealthForm 配置对象
 * 用于驱动通用 MultiCheckboxSubForm 组件
 * 所有文案从 Figma 设计稿中提取：https://www.figma.com/design/YIlGbY9AZh7tW4MZvzFrZN/Aspen-web?node-id=10255-19089&m=dev
 */
export const INDIVIDUAL_SOURCE_OF_WEALTH_CONFIG: IMultiCheckboxSubFormConfig = {
  options: [
    {
      label: () => crossIntl["kyc.v2.option.label.salary.bonus.from.employment"],
      description: () => crossIntl["kyc.v2.option.description.salary.bonus.from.employment"],
      fieldKey: "employmentIncome",
      subFormFields: [
        {
          name: "employerName",
          label: () => crossIntl["kyc.v2.form.label.employer.name"],
          type: "input",
          required: true,
        },
        {
          name: "employerAddress",
          label: () => crossIntl["kyc.v2.form.label.employer.address"],
          type: "input",
          required: true,
        },
        {
          name: "businessSector",
          label: () => crossIntl["kyc.v2.form.label.business.sector"],
          type: "input",
          required: true,
        },
        {
          name: "salaryPerAnnum",
          label: () => crossIntl["kyc.v2.form.label.salary.per.annum.only"],
          type: "input",
          required: true,
        },
        {
          name: "serviceYears",
          label: () => crossIntl["kyc.v2.form.label.years.working.at.employer"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        employerName: "",
        employerAddress: "",
        businessSector: "",
        salaryPerAnnum: "",
        serviceYears: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.self.employment.business.ownership"],
      description: () => crossIntl["kyc.v2.option.description.self.employment.business.ownership"],
      fieldKey: "selfEmployment",
      subFormFields: [
        {
          name: "nameAndAddress",
          label: () => crossIntl["kyc.v2.form.label.name.and.address.of.business"],
          type: "input",
          required: true,
        },
        {
          name: "annualProfit",
          label: () => crossIntl["kyc.v2.form.label.annual.profit.only"],
          type: "input",
          required: true,
        },
        {
          name: "natureOfBusiness",
          label: () => crossIntl["kyc.v2.form.label.nature.of.business"],
          type: "input",
          required: true,
        },
        {
          name: "ownershipPercentage",
          label: () => crossIntl["kyc.v2.form.label.ownership.percentage.in.business"],
          type: "input",
          required: true,
        },
        {
          name: "businessYears",
          label: () => crossIntl["kyc.v2.form.label.business.years.in.operation"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        nameAndAddress: "",
        annualProfit: "",
        natureOfBusiness: "",
        ownershipPercentage: "",
        businessYears: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.sale.of.assets"],
      description: () => crossIntl["kyc.v2.option.description.sale.of.assets"],
      fieldKey: "assetSale",
      subFormFields: [
        {
          name: "assetType",
          label: () => crossIntl["kyc.v2.form.label.type.of.asset"],
          type: "input",
          required: true,
        },
        {
          name: "saleDate",
          label: () => crossIntl["kyc.v2.form.label.date.of.sale"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
        {
          name: "saleAmount",
          label: () => crossIntl["kyc.v2.form.label.total.sale.amount"],
          type: "input",
          required: true,
        },
        {
          name: "holdPeriod",
          label: () => crossIntl["kyc.v2.form.label.how.long.asset.owned"],
          type: "input",
          required: true,
        },
        {
          name: "originatingSource",
          label: () => crossIntl["kyc.v2.form.label.originating.source.of.asset"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        assetType: "",
        saleDate: "",
        saleAmount: "",
        holdPeriod: "",
        originatingSource: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.inheritance"],
      description: () => crossIntl["kyc.v2.option.description.inheritance.wrong"],
      fieldKey: "inheritance",
      subFormFields: [
        {
          name: "deceasedName",
          label: () => crossIntl["kyc.v2.form.label.name.of.deceased"],
          type: "input",
          required: true,
        },
        {
          name: "deathDate",
          label: () => crossIntl["kyc.v2.form.label.date.of.death"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
        {
          name: "relationship",
          label: () => crossIntl["kyc.v2.form.label.relationship.to.customer"],
          type: "input",
          required: true,
        },
        {
          name: "amountReceived",
          label: () => crossIntl["kyc.v2.form.label.inheritance.received"],
          type: "input",
          required: true,
        },
        {
          name: "receivedDate",
          label: () => crossIntl["kyc.v2.form.label.date.of.receipt"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
      ],
      createInitialValue: () => ({
        deceasedName: "",
        deathDate: "",
        relationship: "",
        amountReceived: "",
        receivedDate: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.profits.returns.dividends"],
      description: () => crossIntl["kyc.v2.option.description.profits.returns.dividends"],
      fieldKey: "investmentIncome",
      subFormFields: [
        {
          name: "receiptDate",
          label: () => crossIntl["kyc.v2.form.label.date.of.receipt.of.dividend.profit"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
        {
          name: "receivedAmount",
          label: () => crossIntl["kyc.v2.form.label.total.amount.received"],
          type: "input",
          required: true,
        },
        {
          name: "payingCompanyNameAndTradedAsset",
          label: () =>
            crossIntl["kyc.v2.form.label.name.of.company.paying.dividend.type.of.assets.traded"],
          type: "input",
          required: true,
        },
        {
          name: "holdPeriod",
          label: () =>
            crossIntl[
              "kyc.v2.form.label.length.of.time.shares.held.time.period.managing.portfolio"
            ],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        receiptDate: "",
        receivedAmount: "",
        payingCompanyNameAndTradedAsset: "",
        holdPeriod: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.gifts"],
      description: () => crossIntl["kyc.v2.option.description.gifts"],
      fieldKey: "familySupportGift",
      subFormFields: [
        {
          name: "receivedDate",
          label: () => crossIntl["kyc.v2.form.label.date.of.receipt"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
        {
          name: "receivedAmount",
          label: () => crossIntl["kyc.v2.form.label.total.amount.received"],
          type: "input",
          required: true,
        },
        {
          name: "relationshipToUBO",
          label: () => crossIntl["kyc.v2.form.label.relationship.to.ubo"],
          type: "input",
          required: true,
        },
        {
          name: "giftReason",
          label: () => crossIntl["kyc.v2.form.label.reason.for.gift"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        receivedDate: "",
        receivedAmount: "",
        relationshipToUBO: "",
        giftReason: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.legal.settlements.awards"],
      description: () => crossIntl["kyc.v2.option.description.legal.settlements.awards"],
      fieldKey: "legalSettlement",
      subFormFields: [
        {
          name: "details",
          label: () => crossIntl["kyc.v2.form.label.details.of.legal.settlement"],
          type: "input",
          required: true,
        },
        {
          name: "paymentIssuingParty",
          label: () => crossIntl["kyc.v2.form.label.payment.issuing.party"],
          type: "input",
          required: true,
        },
        {
          name: "settlementAmount",
          label: () => crossIntl["kyc.v2.form.label.settlement.amount"],
          type: "input",
          required: true,
        },
        {
          name: "settlementDate",
          label: () => crossIntl["kyc.v2.form.label.settlement.date"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
      ],
      createInitialValue: () => ({
        details: "",
        paymentIssuingParty: "",
        settlementAmount: "",
        settlementDate: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.winnings.windfall"],
      description: () => crossIntl["kyc.v2.option.description.winnings.windfall"],
      fieldKey: "winnings",
      subFormFields: [
        {
          name: "winningsDate",
          label: () => crossIntl["kyc.v2.form.label.date.of.winning.windfall"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
        {
          name: "windfallNature",
          label: () => crossIntl["kyc.v2.form.label.nature.of.windfall"],
          type: "input",
          required: true,
        },
        {
          name: "windfallAmount",
          label: () => crossIntl["kyc.v2.form.label.windfall.amount"],
          type: "input",
          required: true,
        },
        {
          name: "nameOfIssuingPayment",
          label: () => crossIntl["kyc.v2.form.label.name.of.entity.issuing.payment"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        winningsDate: "",
        windfallNature: "",
        windfallAmount: "",
        nameOfIssuingPayment: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.intellectual.property"],
      description: () => crossIntl["kyc.v2.option.description.intellectual.property"],
      fieldKey: "intellectualProperty",
      subFormFields: [
        {
          name: "type",
          label: () => crossIntl["kyc.v2.form.label.type.of.intellectual.property"],
          type: "input",
          required: true,
        },
        {
          name: "amountReceived",
          label: () => crossIntl["kyc.v2.form.label.fund.received"],
          type: "input",
          required: true,
        },
        {
          name: "dateReceived",
          label: () => crossIntl["kyc.v2.form.label.date.of.receipt.of.funds"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
      ],
      createInitialValue: () => ({
        type: "",
        amountReceived: "",
        dateReceived: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.rental.income"],
      description: () => crossIntl["kyc.v2.option.description.rental.income"],
      fieldKey: "rentalIncome",
      subFormFields: [
        {
          name: "propertyAddress",
          label: () => crossIntl["kyc.v2.form.label.rental.property.address"],
          type: "input",
          required: true,
        },
        {
          name: "annualRentalIncome",
          label: () => crossIntl["kyc.v2.form.label.property.owned.and.rented.period"],
          type: "input",
          required: true,
        },
        {
          name: "periodRented",
          label: () => crossIntl["kyc.v2.form.label.rental.amount"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        propertyAddress: "",
        periodRented: "",
        annualRentalIncome: "",
      }),
    },
    {
      label: () => crossIntl["kyc.other.input"],
      description: () => crossIntl["kyc.v2.option.description.other"],
      fieldKey: "other",
      isOther: true,
      otherPlaceholder: () => crossIntl["kyc.other.input"],
      otherTitleFieldName: "title",
      subFormFields: [
        {
          name: "nature",
          label: () => crossIntl["kyc.v2.form.label.nature.of.source.of.wealth"],
          type: "input",
          required: true,
        },
        {
          name: "receivedDate",
          label: () => crossIntl["kyc.v2.form.label.date.of.receipt"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
        {
          name: "receivedAmount",
          label: () => crossIntl["kyc.v2.form.label.total.amount.received"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        title: "",
        nature: "",
        receivedDate: "",
        receivedAmount: "",
      }),
    },
  ],
};
